import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Stack } from "@/components/atoms/Stack";

/**
 * Returns the themed skeleton background class for loading states
 * @param isDark - Whether dark mode is active
 */
export const getSkeletonBgClass = (isDark: boolean): string =>
  isDark ? "bg-surface-subtle" : "bg-surface-muted";

/**
 * Props for FormLoadingSkeletonView
 */
interface Props {
  /** Whether dark theme is active for skeleton styling */
  isDark: boolean;
}

/**
 * Reusable loading skeleton for form-based screens (intake forms, edit forms).
 * Renders a set of placeholder bars that mimic a form layout while data loads.
 * @param root0 - Component props
 * @param root0.isDark - Whether dark theme is active
 */
const FormLoadingSkeletonView = ({ isDark }: Props) => (
  <Box className="flex-1 p-6">
    <Stack className="gap-8">
      <Stack className="gap-4">
        <Skeleton
          className={`h-10 w-3/4 rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-6 w-full rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-6 w-full rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-6 w-2/3 rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
      </Stack>
      <Stack className="gap-4">
        <Skeleton
          className={`h-32 w-full rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
        <Skeleton
          className={`h-10 w-1/3 rounded-none ${getSkeletonBgClass(isDark)}`}
          isLoaded={false}
        />
      </Stack>
    </Stack>
  </Box>
);

FormLoadingSkeletonView.displayName = "FormLoadingSkeletonView";

export default memo(FormLoadingSkeletonView);
